package com.vizio.redwolf.pay.api

import com.vizio.redwolf.utils.ECommerceEnvironmentConfig
import com.vizio.redwolf.pay.client.ProductApiHttpClient
import com.vizio.redwolf.pay.model.Product
import com.vizio.redwolf.pay.model.ProductCategory
import com.vizio.redwolf.pay.model.ProductCategoryDetails
import com.vizio.redwolf.pay.model.ProductType
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.choosePayEnvironment
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.model.PayFailureResponse
import com.vizio.redwolf.utils.model.VersionResponse
import com.vizio.redwolf.utils.retryRequest
import io.ktor.client.call.NoTransformationFoundException
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.request
import io.ktor.http.isSuccess
import kotlin.js.JsExport

class ProductsApi(
    payEnv: PayEnvironment,
    private val appConfig: AppConfig,
    private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS)
) : Products {
    private val productClient = ProductApiHttpClient(payEnv = payEnv, appConfig = appConfig, clientConfig = clientConfig)

    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig) : this(choosePayEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS))
    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig, clientConfig: ClientConfig) : this(choosePayEnvironment(envConfig), appConfig, clientConfig)
    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig, timeout: Long) : this(choosePayEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS, timeout))

    companion object {
        const val ERROR_CONTENT_TYPE_NOT_SUPPORTED = "NoTransformationFoundException: Response ContentType is not supported"
    }

    /**
     * If getProducts is called without productIds (or an empty list), all products will be returned.
     * To filter by specific products, pass a list of productIds, for example:
     * productApi.getProducts(listOf("R5-D4", "C-3PO", "R2-D2"))
     *
     */
    override suspend fun getProducts(productIds: List<String>): ProductsResult<List<Product>> {
        return retryProductsRequest {
            val getProductsResp = productClient.getProducts(productIds)
            handleProductsHttpResult(getProductsResp)
        }
    }

    override suspend fun getCategories(): ProductsResult<List<ProductCategory>> {
        return retryProductsRequest {
            val getCategoriesResp = productClient.getCategories()
            handleProductsHttpResult(getCategoriesResp)
        }
    }

    override suspend fun getProductsByCategory(categoryId: String): ProductsResult<List<Product>> {
        return retryProductsRequest {
            val getProductsResp = productClient.getProductsByCategory(categoryId)
            handleProductsHttpResult(getProductsResp)
        }
    }

    override suspend fun getCategoryDetails(categoryId: String): ProductsResult<ProductCategoryDetails> {
        return retryProductsRequest {
            val getCategoryResp = productClient.getCategory(categoryId)
            handleProductsHttpResult(getCategoryResp)
        }
    }

    override suspend fun getProductTypes(): ProductsResult<List<ProductType>> {
        return retryProductsRequest {
            val getProductTypesResp = productClient.getProductTypes()
            handleProductsHttpResult(getProductTypesResp)
        }
    }

    override suspend fun getVersion(): ProductsResult<VersionResponse> {
        return retryProductsRequest {
            val getVersionResponse = productClient.getVersion()
            handleProductsHttpResult(getVersionResponse)
        }
    }


    override suspend fun getProductById(id: String): ProductsResult<Product> {
        return retryProductsRequest {
            val getProductResp = productClient.getProductById(id)
            handleProductsHttpResult(getProductResp)
        }
    }

    private suspend inline fun <T : HttpResponse, reified R : Any> handleProductsHttpResult(httpResult: HttpResult<T>): ProductsResult<R> {
        return when (httpResult) {
            is HttpResult.Success -> {
                val httpResponse: HttpResponse = httpResult.data
                val headers = ResponseHeaders.Valid(httpResponse.status.value)
                val requestUrl: String = httpResponse.request.url.toString()

                if (httpResponse.status.isSuccess()) {
                    try {
                        ProductsResult.Success(headers, httpResponse.body<R>())
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        ProductsResult.Error(headers, ex)
                    }
                } else {
                    try {
                        val payFailureResponse = httpResponse.body<PayFailureResponse>()
                        ProductsResult.Failed(headers, payFailureResponse)
                    } catch (ex: NoTransformationFoundException) {
                        ex.printStackTrace()
                        val exception = Exception("$ERROR_CONTENT_TYPE_NOT_SUPPORTED:$requestUrl")
                        ProductsResult.Error(headers, exception)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        ProductsResult.Error(headers, ex)
                    }
                }
            }
            is HttpResult.Error -> {
                val headers = ResponseHeaders.Invalid
                if (httpResult.exception is TimeoutException)
                    ProductsResult.Timeout(headers, httpResult.exception)
                else
                    ProductsResult.Error(headers, httpResult.exception)
            }
        }
    }

    private fun <T: Any> isProductsSuccess(result: T): Boolean {
        return result is ProductsResult.Success<*>
    }

    private suspend inline fun <T: Any> retryProductsRequest(block: () -> T): T {
        return retryRequest(clientConfig, block, isSuccess = ::isProductsSuccess)
    }
}

@JsExport
sealed class ProductsResult<out T : Any> {
    data class Success<out T : Any>(val headers: ResponseHeaders, val data: T) : ProductsResult<T>()
    data class Failed(val headers: ResponseHeaders, val response: PayFailureResponse) : ProductsResult<Nothing>()
    data class Error(val headers: ResponseHeaders, val exception: Throwable) : ProductsResult<Nothing>()
    data class Timeout(val headers: ResponseHeaders, val exception: Throwable) : ProductsResult<Nothing>()

    fun <R> fold (
        onSuccess: (Success: Success<T>) -> R,
        onFailed: (Failed: Failed) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is Success -> onSuccess(Success(this.headers, this.data))
            is Failed -> onFailed(Failed(this.headers, this.response))
            is Error -> onError(Error(this.headers, this.exception))
            is Timeout -> onTimeout(Timeout(this.headers, this.exception))
        }
    }
}
